import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, CircleDot, Disc, Sparkles, Zap } from "lucide-react";
import { SERVICE_CATEGORIES } from "@/data/services";

const ICONS = { wheels: Disc, steering: CircleDot, interior: Sparkles, lighting: Zap };

const ServicesGrid = ({ onAdd }) => {
  const [active, setActive] = useState(SERVICE_CATEGORIES[0].id);
  const cat = SERVICE_CATEGORIES.find((c) => c.id === active);

  return (
    <section id="services" data-testid="services-section" className="relative py-20 sm:py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-volt mb-4">
            01 / Services &amp; Pricing
          </div>
          <h2 className="font-syne font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-100 max-w-3xl leading-tight">
            Every surface. <span className="text-outline">Rebuilt to spec.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-xl">
            Standard rates shown — all prices <span className="text-volt font-semibold">+ GST</span>.
            Add services to the instant estimate as you browse.
          </p>
        </motion.div>

        <div className="mt-12 flex flex-wrap gap-3" role="tablist" aria-label="Service categories">
          {SERVICE_CATEGORIES.map((c) => {
            const Icon = ICONS[c.id];
            const isActive = c.id === active;
            return (
              <button
                key={c.id}
                data-testid={`service-category-tab-${c.id}`}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(c.id)}
                className={`flex items-center gap-2.5 rounded-full border px-5 py-2.5 font-mono2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive
                    ? "border-volt bg-volt/10 text-volt"
                    : "border-edge text-slate-400 hover:text-slate-100 hover:border-slate-500"
                }`}
              >
                <Icon size={14} /> {c.category}
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10"
          >
            <p className="text-sm text-slate-500 mb-8 max-w-2xl">{cat.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cat.items.map((item, idx) => (
                <motion.article
                  key={item.name}
                  data-testid={`service-item-card-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: idx * 0.07, ease: [0.16, 1, 0.3, 1] }}
                  className="group relative bg-panel border border-edge rounded-xl p-6 sm:p-7 hover:border-volt/60 hover:shadow-[0_0_28px_rgba(0,240,255,0.14)] transition-[border-color,box-shadow] duration-400 flex flex-col"
                >
                  <div className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">
                    {cat.code}.{String(idx + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-syne font-bold uppercase tracking-wide text-lg text-slate-100 group-hover:text-volt transition-colors duration-300">
                    {item.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed flex-1">{item.desc}</p>
                  <div className="mt-6 flex items-end justify-between">
                    <div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-syne font-extrabold text-3xl text-slate-50">${item.price}</span>
                        <span className="font-mono2 text-[10px] uppercase tracking-widest text-volt">+ GST</span>
                      </div>
                      <div className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                        {item.unit}
                      </div>
                    </div>
                    <button
                      data-testid={`add-to-estimate-btn-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => onAdd({ ...item, category: cat.category })}
                      aria-label={`Add ${item.name} to estimate`}
                      className="w-10 h-10 rounded-full border border-edge flex items-center justify-center text-slate-300 hover:bg-volt hover:text-carbon hover:border-volt transition-colors duration-300"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </motion.article>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ServicesGrid;
